import React from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  SelectProps,
  FormHelperText,
  Box,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  getStatusesByCategory, 
  getStatusConfig, 
  getStatusColor,
  getStatusLabel 
} from '../../utils/reportPortalToolkit';
import StatusChip from './StatusChip';

interface StatusSelectorProps extends Omit<SelectProps, 'onChange'> {
  value: string;
  onChange: (status: string) => void;
  category?: string;
  label?: string;
  helperText?: string;
  showPreview?: boolean;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  showShortLabels?: boolean;
  disabled?: boolean;
  error?: boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({
  value,
  onChange,
  category,
  label = 'Status',
  helperText,
  showPreview = true,
  size = 'medium',
  variant = 'outlined',
  showShortLabels = false,
  disabled = false,
  error = false,
  ...selectProps
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const themeMode = isDarkMode ? 'dark' : 'light';

  // Get statuses based on category or all if no category specified
  const statuses = category ? getStatusesByCategory(category) : Object.values(getStatusConfig('') || {});
  
  // Sort by priority
  const sortedStatuses = statuses.sort((a, b) => a.priority - b.priority);

  const handleChange = (event: any) => {
    onChange(event.target.value);
  };

  return (
    <FormControl 
      fullWidth 
      error={error}
      disabled={disabled}
      size={size}
      variant={variant}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={handleChange}
        {...selectProps}
      >
        {sortedStatuses.map((statusConfig) => (
          <MenuItem 
            key={statusConfig.label} 
            value={statusConfig.label.toLowerCase().replace(/\s+/g, '_')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1
            }}
          >
            {showPreview && (
              <StatusChip
                status={statusConfig.label.toLowerCase().replace(/\s+/g, '_')}
                size="small"
                variant="filled"
                showShortLabel={showShortLabels}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" component="span">
                {showShortLabels && statusConfig.shortLabel 
                  ? statusConfig.shortLabel 
                  : statusConfig.label
                }
              </Typography>
              {statusConfig.description && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {statusConfig.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default StatusSelector;
